import { useSyncExternalStore } from "react";
import type { Lang } from "./lib/dates";

const en = {
  appName: "Let's do it",
  addTask: "Add task",
  search: "Search",
  inbox: "Inbox",
  today: "Today",
  upcoming: "Upcoming",
  filtersLabels: "Filters & Labels",
  completed: "Completed",
  favorites: "Favorites",
  myProjects: "My Projects",
  projects: "Projects",
  settings: "Settings",
  logout: "Log out",
  theme: "Theme",
  language: "Language",
  overdue: "Overdue",
  reschedule: "Reschedule",
  noDate: "No date",
  noSection: "(No section)",
  addSection: "Add section",
  sectionName: "Name this section",
  cancel: "Cancel",
  save: "Save",
  add: "Add",
  delete: "Delete",
  edit: "Edit",
  rename: "Rename",
  archive: "Archive",
  taskName: "Task name",
  description: "Description",
  dueDate: "Date",
  deadline: "Deadline",
  priority: "Priority",
  labels: "Labels",
  reminders: "Reminders",
  assignee: "Assignee",
  unassigned: "Unassigned",
  project: "Project",
  section: "Section",
  subTasks: "Sub-tasks",
  addSubTask: "Add sub-task",
  comments: "Comments",
  comment: "Comment",
  attach: "Attach file",
  uploading: "Uploading…",
  taskCompleted: "1 task completed",
  undo: "Undo",
  view: "View",
  layout: "Layout",
  list: "List",
  board: "Board",
  calendar: "Calendar",
  grouping: "Grouping",
  sorting: "Sorting",
  none: "None",
  manual: "Manual (default)",
  showCompleted: "Completed tasks",
  sortDate: "Date",
  sortAdded: "Date added",
  sortPriority: "Priority",
  sortName: "Name",
  groupDate: "Date",
  groupAdded: "Date added",
  groupPriority: "Priority",
  groupLabel: "Label",
  groupProject: "Project",
  ascending: "Ascending",
  descending: "Descending",
  week: "Week",
  month: "Month",
  myFilters: "My Filters",
  addFilter: "Add filter",
  addLabel: "Add label",
  name: "Name",
  query: "Filter query",
  color: "Color",
  addToFavorites: "Add to favorites",
  removeFromFavorites: "Remove from favorites",
  addProject: "Add project",
  editProject: "Edit project",
  addProjectAbove: "Add project above",
  addProjectBelow: "Add project below",
  deleteProjectConfirm: "Delete {name}? All its tasks will be deleted.",
  deleteTaskConfirm: "Delete task?",
  deleteConfirm: "Delete {name}?",
  parentProject: "Parent project",
  noParent: "No parent",
  workspaces: "Workspaces",
  emptyToday: "Enjoy your day! Everything's done.",
  emptyProject: "No tasks here yet.",
  emptyInbox: "Your inbox is clear.",
  emptySearch: "No results",
  searchPlaceholder: "Search or type a command…",
  tasks: "Tasks",
  loadMore: "Load more",
  noCompleted: "No completed tasks yet.",
  loginTitle: "Log in",
  signupTitle: "Sign up",
  email: "Email",
  password: "Password",
  fullName: "Your name",
  inviteCode: "Invite code",
  loginButton: "Log in",
  signupButton: "Sign up with email",
  useToken: "Use an API token",
  tokenLabel: "API token",
  tokenHint: "Paste a Let's do it API token (Settings → Integrations, or `user:create`).",
  continue: "Continue",
  noAccount: "Don't have an account?",
  haveAccount: "Already signed up?",
  wrongCredentials: "Wrong email or password.",
  loginUnavailable: "Email login isn't available on this server yet. Use an API token instead.",
  server: "Server",
  invalidToken: "That token didn't work.",
  account: "Account",
  general: "General",
  integrations: "Integrations",
  timezone: "Time zone",
  startDay: "Start of the week",
  dateFormat: "Date format",
  timeFormat: "Time format",
  light: "Light",
  dark: "Dark",
  system: "Sync with system",
  apiTokens: "API tokens",
  createToken: "Create token",
  tokenName: "Token name",
  revoke: "Revoke",
  newTokenOnce: "Copy this token now. It won't be shown again.",
  current: "current",
  sessions: "Sessions",
  lastUsed: "Last used",
  never: "never",
  shortcuts: "Keyboard shortcuts",
  offline: "Offline — changes will sync when you're back online.",
  syncError: "Couldn't save: {error}",
  more: "More",
  moveTo: "Move to…",
  copyLink: "Copy link to task",
  setDate: "Set date",
  typeDate: "Type a date",
  tomorrow: "Tomorrow",
  thisWeekend: "This weekend",
  nextWeek: "Next week",
  time: "Time",
  repeat: "Repeat",
  addReminder: "Add reminder",
  reminderPlaceholder: "e.g. 30 min before, tomorrow 9am",
  noReminders: "No reminders",
  priorityN: "Priority {n}",
  quickAddPlaceholder: "e.g. Buy milk tomorrow at 5pm p1 #Errands @home",
  filterError: "Filter error: {error}",
  filterResults: "Tasks",
  label: "Label",
  filter: "Filter",
  completedOn: "Completed {date}",
  saving: "Saving…",
  goToday: "Today",
  previous: "Previous",
  next: "Next",
  collapse: "Collapse",
  expand: "Expand",
  openSidebar: "Open sidebar",
  closeSidebar: "Close sidebar",
  close: "Close",
  sharedWith: "Shared",
  unknownUser: "Someone",
  justNow: "just now",
  welcome: "Welcome back",
  orgTagline: "Organize your work and life, finally.",
  passwordHint: "At least 8 characters",
  noTasks: "No tasks",
  inProject: "in {name}",
} as const;

export type MessageKey = keyof typeof en;

const zh: Record<MessageKey, string> = {
  appName: "Let's do it",
  addTask: "添加任务",
  search: "搜索",
  inbox: "收件箱",
  today: "今天",
  upcoming: "即将到来",
  filtersLabels: "过滤器和标签",
  completed: "已完成",
  favorites: "收藏",
  myProjects: "我的项目",
  projects: "项目",
  settings: "设置",
  logout: "退出登录",
  theme: "主题",
  language: "语言",
  overdue: "已过期",
  reschedule: "重新安排",
  noDate: "无日期",
  noSection: "（无分区）",
  addSection: "添加分区",
  sectionName: "为分区命名",
  cancel: "取消",
  save: "保存",
  add: "添加",
  delete: "删除",
  edit: "编辑",
  rename: "重命名",
  archive: "归档",
  taskName: "任务名称",
  description: "描述",
  dueDate: "日期",
  deadline: "截止日期",
  priority: "优先级",
  labels: "标签",
  reminders: "提醒",
  assignee: "负责人",
  unassigned: "未指派",
  project: "项目",
  section: "分区",
  subTasks: "子任务",
  addSubTask: "添加子任务",
  comments: "评论",
  comment: "评论",
  attach: "添加附件",
  uploading: "正在上传…",
  taskCompleted: "已完成 1 个任务",
  undo: "撤销",
  view: "视图",
  layout: "布局",
  list: "列表",
  board: "看板",
  calendar: "日历",
  grouping: "分组",
  sorting: "排序",
  none: "无",
  manual: "手动（默认）",
  showCompleted: "已完成的任务",
  sortDate: "日期",
  sortAdded: "添加日期",
  sortPriority: "优先级",
  sortName: "名称",
  groupDate: "日期",
  groupAdded: "添加日期",
  groupPriority: "优先级",
  groupLabel: "标签",
  groupProject: "项目",
  ascending: "升序",
  descending: "降序",
  week: "周",
  month: "月",
  myFilters: "我的过滤器",
  addFilter: "添加过滤器",
  addLabel: "添加标签",
  name: "名称",
  query: "过滤查询",
  color: "颜色",
  addToFavorites: "添加到收藏",
  removeFromFavorites: "从收藏中移除",
  addProject: "添加项目",
  editProject: "编辑项目",
  addProjectAbove: "在上方添加项目",
  addProjectBelow: "在下方添加项目",
  deleteProjectConfirm: "删除「{name}」？其中所有任务都会被删除。",
  deleteTaskConfirm: "删除任务？",
  deleteConfirm: "删除「{name}」？",
  parentProject: "父项目",
  noParent: "无父项目",
  workspaces: "工作区",
  emptyToday: "享受这一天吧！今天的任务都完成了。",
  emptyProject: "这里还没有任务。",
  emptyInbox: "收件箱已清空。",
  emptySearch: "没有结果",
  searchPlaceholder: "搜索任务或项目…",
  tasks: "任务",
  loadMore: "加载更多",
  noCompleted: "还没有已完成的任务。",
  loginTitle: "登录",
  signupTitle: "注册",
  email: "邮箱",
  password: "密码",
  fullName: "你的名字",
  inviteCode: "邀请码",
  loginButton: "登录",
  signupButton: "用邮箱注册",
  useToken: "使用 API token 登录",
  tokenLabel: "API token",
  tokenHint: "粘贴 Let's do it API token（设置 → 集成，或管理员 `user:create` 生成的 token）。",
  continue: "继续",
  noAccount: "还没有账号？",
  haveAccount: "已有账号？",
  wrongCredentials: "邮箱或密码错误。",
  loginUnavailable: "该服务器暂不支持邮箱登录，请使用 API token。",
  server: "服务器",
  invalidToken: "这个 token 无效。",
  account: "账户",
  general: "通用",
  integrations: "集成",
  timezone: "时区",
  startDay: "每周开始于",
  dateFormat: "日期格式",
  timeFormat: "时间格式",
  light: "浅色",
  dark: "深色",
  system: "跟随系统",
  apiTokens: "API token",
  createToken: "创建 token",
  tokenName: "token 名称",
  revoke: "吊销",
  newTokenOnce: "请立即复制这个 token，它不会再次显示。",
  current: "当前",
  sessions: "登录会话",
  lastUsed: "最近使用",
  never: "从未",
  shortcuts: "键盘快捷键",
  offline: "离线中——恢复联网后会自动同步。",
  syncError: "保存失败：{error}",
  more: "更多",
  moveTo: "移动到…",
  copyLink: "复制任务链接",
  setDate: "设置日期",
  typeDate: "输入日期",
  tomorrow: "明天",
  thisWeekend: "本周末",
  nextWeek: "下周",
  time: "时间",
  repeat: "重复",
  addReminder: "添加提醒",
  reminderPlaceholder: "例如：提前30分钟、明天上午9点",
  noReminders: "没有提醒",
  priorityN: "优先级 {n}",
  quickAddPlaceholder: "例如：明天下午5点买牛奶 p1 #购物 @家里",
  filterError: "过滤器错误：{error}",
  filterResults: "任务",
  label: "标签",
  filter: "过滤器",
  completedOn: "完成于 {date}",
  saving: "正在保存…",
  goToday: "今天",
  previous: "上一个",
  next: "下一个",
  collapse: "折叠",
  expand: "展开",
  openSidebar: "打开侧边栏",
  closeSidebar: "关闭侧边栏",
  close: "关闭",
  sharedWith: "共享",
  unknownUser: "某人",
  justNow: "刚刚",
  welcome: "欢迎回来",
  orgTagline: "整理你的工作和生活。",
  passwordHint: "至少 8 个字符",
  noTasks: "没有任务",
  inProject: "在 {name}",
};

const dicts: Record<Lang, Record<MessageKey, string>> = { en, zh };

export function browserLang(): Lang {
  const nav = typeof navigator !== "undefined" ? navigator.language : "en";
  return nav.toLowerCase().startsWith("zh") ? "zh" : "en";
}

export const normalizeLang = (lang: string | null | undefined): Lang =>
  lang?.toLowerCase().startsWith("zh") ? "zh" : "en";

let current: Lang = (() => {
  try {
    const stored = localStorage.getItem("ld.lang");
    if (stored) return normalizeLang(stored);
  } catch {
    // ignore
  }
  return browserLang();
})();
const listeners = new Set<() => void>();

export function setLang(lang: Lang) {
  if (lang === current) return;
  current = lang;
  try {
    localStorage.setItem("ld.lang", lang);
  } catch {
    // ignore
  }
  document.documentElement.lang = lang === "zh" ? "zh-CN" : "en";
  for (const l of listeners) l();
}

export const getLang = () => current;

export function translate(lang: Lang, key: MessageKey, vars?: Record<string, string | number>): string {
  let s = dicts[lang][key];
  if (vars) for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, String(v));
  return s;
}

export function useLang(): Lang {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
  );
}

export function useT() {
  const lang = useLang();
  return (key: MessageKey, vars?: Record<string, string | number>) => translate(lang, key, vars);
}
